/* The figures, on the way in.
 *
 * ERPNext ships 85 number cards as metadata, so these are as generic as the
 * forms: a card a tenant adds tomorrow appears with no change here.
 *
 * Two rules, both learned from dashboards that are worse than nothing:
 *
 *   - **A tile that cannot be worked out says so.** A blank where a number
 *     should be reads as zero, and zero is a fact. "Could not be worked out"
 *     is a different fact.
 *   - **The number is the biggest thing on the tile.** A card whose label is
 *     louder than its figure is a label with a number attached.
 */
import { useEffect, useState } from 'react'
import { useErp } from './context'
import { formatCurrency, formatNumber } from './format'
import type { NumberCard as Card } from './types'

export function Cards({ module, names, onOpen }: {
  module?: string
  names?: string[]
  onOpen?: (doctype: string) => void
}) {
  const erp = useErp()
  const [cards, setCards] = useState<Card[] | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    const controller = new AbortController()
    setCards(null); setFailed(false)
    erp.numberCards({ module, names }, controller.signal)
      .then((r) => setCards(r.cards ?? []))
      .catch((e) => { if ((e as Error).name !== 'AbortError') setFailed(true) })
    return () => controller.abort()
  }, [erp, module, names?.join(',')])

  if (failed) return null
  if (cards === null) {
    return (
      <div className="erp-cards">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="card card-pad"><div className="skel" style={{ height: 44 }} /></div>
        ))}
      </div>
    )
  }
  if (cards.length === 0) return null

  return (
    <div className="erp-cards">
      {cards.map((c) => {
        const clickable = Boolean(c.document_type && onOpen)
        const Tag = clickable ? 'button' : 'div'
        return (
          <Tag key={c.name} className="card card-pad" style={{ textAlign: 'left' }}
               {...(clickable ? { onClick: () => onOpen!(c.document_type!) } : {})}>
            <div className="small muted truncate" title={c.name}>{c.label || c.name}</div>
            {/* Proportional figures on purpose: `tabular-nums` gives every
                digit the width of a zero, which reads loose at this size.
                Tabular belongs in columns that have to line up. */}
            <div style={{ fontSize: 26, fontWeight: 650, letterSpacing: '-0.02em', marginTop: 4,
                          color: c.ok === false ? 'var(--ink-3)' : undefined }}>
              {c.ok === false ? '—' : figure(c)}
            </div>
            <div className="small muted truncate" style={{ marginTop: 2 }}>
              {c.ok === false ? (c.why ?? 'Could not be worked out') : (c.document_type ?? '')}
            </div>
          </Tag>
        )
      })}
    </div>
  )
}

/** A Count is a count; a Sum of an amount is money. Guessing from the value
 *  would format a voucher count as rupees the moment it passed 1,000. */
function figure(card: Card): string {
  const value = card.value
  if (value === null || value === undefined) return '—'
  if (card.function === 'Count') return formatNumber(value, 0)
  return formatCurrency(value)
}
